import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatTimeAgo } from '@tendedloop/shared/i18n';
import { motion, AnimatePresence } from 'framer-motion';
import {
  QrCode,
  AlertTriangle,
  RefreshCw,
  Trees,
  Heart,
  Compass,
  X,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Search,
} from 'lucide-react';
import type { AnimalHealthState } from '@tendedloop/shared';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import AnimalCard from '@/components/AnimalCard';
import AnimalDetailModal from '@/components/AnimalDetailModal';
import { HEALTH_COLORS, HEALTH_LABELS } from '@/utils/animalHealth';

interface ReserveSpecies {
  slug: string;
  name: string;
  habitat: string;
  fieldNote: string;
  imagePath: string;
  thumbPath: string;
  amenityName: string;
  locationName: string | null;
  buildingName: string;
  healthState: AnimalHealthState;
  lastScannedAt: string;
  discoveredAt: string;
  scanCount: number;
}

interface ReserveStats {
  total: number;
  thriving: number;
  needsAttention: number;
  atRisk: number;
}

interface ReserveResponse {
  species: ReserveSpecies[];
  stats: ReserveStats;
}

interface AnimalDetail {
  slug: string;
  name: string;
  habitat: string;
  fieldNote: string;
  imagePath: string;
  thumbPath: string;
  healthState: AnimalHealthState;
  amenityName: string;
  locationName: string | null;
  buildingName: string;
  lastScannedAt: string;
  discoveredAt: string;
  scanCount: number;
  thisMonthScans: number;
  scanDates: string[];
}

interface CatalogSpecies {
  id: string;
  slug: string;
  name: string;
  habitat: string;
  fieldNote: string;
  imagePath: string;
  thumbPath: string;
  discovered: boolean;
}

interface CatalogResponse {
  species: CatalogSpecies[];
  totalDiscovered: number;
  totalSpecies: number;
}

const TOTAL_SPECIES = 240;

const HABITATS = ['forest', 'mountain', 'savanna', 'ocean', 'jungle', 'exotic', 'arctic', 'desert'] as const;

const SECTION_CONFIG: {
  key: string;
  states: AnimalHealthState[];
  color: string;
  dotColor: string;
}[] = [
  {
    key: 'thriving',
    states: ['thriving'],
    color: 'text-emerald-600',
    dotColor: HEALTH_COLORS['thriving'],
  },
  {
    key: 'fading',
    states: ['fading'],
    color: 'text-amber-600',
    dotColor: HEALTH_COLORS['fading'],
  },
  {
    key: 'atRisk',
    states: ['at-risk'],
    color: 'text-red-500',
    dotColor: HEALTH_COLORS['at-risk'],
  },
];

export default function ReservePage() {
  const { t, i18n } = useTranslation('globe');
  const navigate = useNavigate();
  const { isAuthenticated, scout } = useAuthStore();

  const [activeTab, setActiveTab] = useState<'globe' | 'catalog'>('globe');
  const [species, setSpecies] = useState<ReserveSpecies[]>([]);
  const [stats, setStats] = useState<ReserveStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState<AnimalDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cacheAge, setCacheAge] = useState<number | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('globe_onboarded'));
  const [onboardingStep, setOnboardingStep] = useState(0);

  // Catalog state
  const [catalogSpecies, setCatalogSpecies] = useState<CatalogSpecies[] | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [expandedHabitats, setExpandedHabitats] = useState<Set<string>>(new Set(HABITATS));
  const catalogFetched = useRef(false);

  const dismissOnboarding = useCallback(() => {
    if (onboardingStep >= 2) {
      window.gtag?.('event', 'globe_onboarding_complete');
    } else {
      window.gtag?.('event', 'globe_onboarding_dismiss', { step: onboardingStep });
    }
    localStorage.setItem('globe_onboarded', '1');
    setShowOnboarding(false);
  }, [onboardingStep]);

  useEffect(() => {
    if (showOnboarding) {
      window.gtag?.('event', 'globe_onboarding_start');
    }
  }, []); // only on mount

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (scout?.globeEnabled === false) return;
    loadReserve();
  }, [isAuthenticated, navigate, scout?.globeEnabled]);

  async function loadReserve() {
    try {
      setLoadError(false);
      setCacheAge(null);
      setLoading(true);
      const data = await api.get<ReserveResponse>('/scout/globe/reserve');
      setSpecies(data.species);
      setStats(data.stats);
      localStorage.setItem(
        'globe_reserve_cache',
        JSON.stringify({
          species: data.species,
          stats: data.stats,
          cachedAt: Date.now(),
        }),
      );
      window.gtag?.('event', 'globe_reserve_view', {
        species_count: data.stats.total,
        health_summary: `${data.stats.thriving}t/${data.stats.needsAttention}f/${data.stats.atRisk}a`,
      });
    } catch {
      const cached = localStorage.getItem('globe_reserve_cache');
      if (cached) {
        try {
          const { species: cachedSpecies, stats: cachedStats, cachedAt } = JSON.parse(cached);
          setSpecies(cachedSpecies);
          setStats(cachedStats);
          setCacheAge(cachedAt);
          setLoadError(false);
        } catch {
          setLoadError(true);
        }
      } else {
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadCatalog() {
    if (catalogFetched.current) return;
    try {
      setCatalogError(false);
      setCatalogLoading(true);
      const data = await api.get<CatalogResponse>('/scout/globe/catalog');
      setCatalogSpecies(data.species);
      catalogFetched.current = true;
    } catch {
      setCatalogError(true);
    } finally {
      setCatalogLoading(false);
    }
  }

  function handleTabChange(tab: 'globe' | 'catalog') {
    setActiveTab(tab);
    if (tab === 'catalog' && !catalogFetched.current) {
      loadCatalog();
    }
  }

  function toggleHabitat(habitat: string) {
    setExpandedHabitats((prev) => {
      const next = new Set(prev);
      if (next.has(habitat)) next.delete(habitat);
      else next.add(habitat);
      return next;
    });
  }

  async function openDetail(slug: string) {
    try {
      setDetailLoading(true);
      const data = await api.get<AnimalDetail>(`/scout/globe/reserve/${slug}`);
      setSelectedAnimal(data);
    } catch {
      // Silently fail - user can retry by tapping again
    } finally {
      setDetailLoading(false);
    }
  }

  const rangerLevel = scout?.level ?? 1;
  const totalDiscovered = stats?.total ?? 0;
  const progressPercent = Math.min((totalDiscovered / TOTAL_SPECIES) * 100, 100);

  // Group species by section
  const sections = SECTION_CONFIG.map((cfg) => ({
    ...cfg,
    animals: species.filter((s) => cfg.states.includes(s.healthState)),
  })).filter((s) => s.animals.length > 0);

  if (scout?.globeEnabled === false) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center">
        <Trees size={48} className="text-forest mb-4" />
        <h2 className="text-lg font-bold text-foreground mb-2">
          {t('reserve.comingSoonTitle', { defaultValue: 'Coming soon' })}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('reserve.comingSoonMessage', {
            defaultValue: 'The Globe is not yet available for your account. Stay tuned.',
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Header */}
      <div className="bg-forest px-4 pb-5 pt-6">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-white">{t('reserve.title', { defaultValue: 'My Globe' })}</h1>
          <div className="flex items-center gap-2">
            {totalDiscovered > 0 && (
              <button
                type="button"
                onClick={async () => {
                  window.gtag?.('event', 'globe_share_card', { species_count: totalDiscovered });
                  try {
                    const resp = await fetch(
                      `${import.meta.env.VITE_API_URL || 'https://api.tendedloop.com'}/api/scout/globe/share-card`,
                      {
                        headers: { Authorization: `Bearer ${localStorage.getItem('scout_accessToken')}` },
                      },
                    );
                    if (resp.ok) {
                      const blob = await resp.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'my-globe.png';
                      a.click();
                      URL.revokeObjectURL(url);
                    }
                  } catch {
                    /* silent */
                  }
                }}
                className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-medium text-white"
                aria-label={t('share.button', { defaultValue: 'Share your Globe' })}
              >
                {t('share.button', { defaultValue: 'Share' })}
              </button>
            )}
            <div className="flex items-center gap-1.5 rounded-full bg-white/20 ps-1 pe-2.5 py-0.5">
              <img src={`/images/ranks/rank-${rangerLevel}.png`} alt="" className="h-5 w-5 rounded-full" />
              <span className="text-xs font-medium text-white">
                {t(`rank.${rangerLevel}`, { defaultValue: `Level ${rangerLevel}` })}
              </span>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-3">
          <div className="flex items-end justify-between text-xs text-white/80">
            <span>
              {t('reserve.subtitle', {
                defaultValue: '{{count}} species discovered',
                count: totalDiscovered,
              })}
            </span>
            <span>
              {totalDiscovered}/{TOTAL_SPECIES}
            </span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/20">
            <div
              className="h-full rounded-full bg-gold transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex border-b border-border bg-background">
        <button
          type="button"
          onClick={() => handleTabChange('globe')}
          className={`flex-1 py-2.5 text-center text-sm font-medium transition-colors ${
            activeTab === 'globe' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'
          }`}
        >
          {t('catalog.myGlobe', { defaultValue: 'My Globe' })}
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('catalog')}
          className={`flex-1 py-2.5 text-center text-sm font-medium transition-colors ${
            activeTab === 'catalog' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'
          }`}
        >
          {t('catalog.title', { defaultValue: 'Catalog' })}
        </button>
      </div>

      {/* Cache banner */}
      {cacheAge && (
        <div className="mx-4 mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
          {t('reserve.cachedData', {
            defaultValue: 'Showing cached data from {{time}}. Pull to refresh.',
            time: formatTimeAgo(cacheAge, i18n.language),
          })}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 px-4 py-4">
        {activeTab === 'globe' && (
          <>
            {/* Loading */}
            {loading && (
              <div className="grid grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5 rounded-xl bg-card p-2">
                    <div className="h-20 w-20 animate-pulse rounded-lg bg-muted" />
                    <div className="h-3 w-14 animate-pulse rounded bg-muted" />
                    <div className="h-2.5 w-10 animate-pulse rounded bg-muted" />
                  </div>
                ))}
              </div>
            )}

            {/* Error */}
            {!loading && loadError && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <AlertTriangle size={32} className="text-amber-400" />
                <p className="text-sm text-muted-foreground">
                  {t('error.loadFailed', { defaultValue: "Couldn't load your reserve. Pull to retry." })}
                </p>
                <button
                  type="button"
                  onClick={loadReserve}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-forest px-4 py-2 text-sm font-medium text-white"
                >
                  <RefreshCw size={14} />
                  {t('common:retry', { defaultValue: 'Retry' })}
                </button>
              </div>
            )}

            {/* Empty state */}
            {!loading && !loadError && species.length === 0 && (
              <div className="flex flex-col items-center gap-4 py-16 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-wash">
                  <QrCode size={28} className="text-forest" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">
                    {t('reserve.emptyTitle', { defaultValue: 'Your Globe is empty' })}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('reserve.emptyDescription', {
                      defaultValue: 'Scan a QR code to discover your first species.',
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/scan')}
                  className="inline-flex items-center gap-2 rounded-xl bg-forest px-5 py-2.5 text-sm font-semibold text-white"
                >
                  <QrCode size={16} />
                  {t('reserve.emptyAction', { defaultValue: 'Open Scanner' })}
                </button>
              </div>
            )}

            {/* Populated sections */}
            {!loading && !loadError && species.length > 0 && (
              <div className="flex flex-col gap-6">
                {sections.map((section) => (
                  <div key={section.key}>
                    <div className="mb-2.5 flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${section.dotColor}`} aria-hidden="true" />
                      <h3 className={`text-sm font-semibold ${section.color}`}>
                        {t(`reserve.section${section.key.charAt(0).toUpperCase() + section.key.slice(1)}`, {
                          defaultValue:
                            section.key === 'thriving' ? 'Thriving' : section.key === 'fading' ? 'Fading' : 'At risk',
                        })}
                      </h3>
                      <span className="text-xs text-muted-foreground">({section.animals.length})</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {section.animals.map((animal) => (
                        <AnimalCard
                          key={animal.slug}
                          slug={animal.slug}
                          name={animal.name}
                          thumbPath={animal.thumbPath}
                          amenityName={animal.amenityName}
                          healthState={animal.healthState}
                          onClick={() => openDetail(animal.slug)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'catalog' && (
          <>
            {/* Catalog Loading */}
            {catalogLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-forest border-t-transparent" />
              </div>
            )}

            {/* Catalog Error */}
            {!catalogLoading && catalogError && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <AlertTriangle size={32} className="text-amber-400" />
                <p className="text-sm text-muted-foreground">
                  {t('error.catalogFailed', { defaultValue: "Couldn't load the species catalog." })}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    catalogFetched.current = false;
                    loadCatalog();
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-forest px-4 py-2 text-sm font-medium text-white"
                >
                  <RefreshCw size={14} />
                  {t('common:retry', { defaultValue: 'Retry' })}
                </button>
              </div>
            )}

            {/* Catalog Content */}
            {!catalogLoading && !catalogError && catalogSpecies && (
              <div className="flex flex-col gap-4">
                {HABITATS.map((habitat) => {
                  const habitatSpecies = catalogSpecies.filter((s) => s.habitat === habitat);
                  if (habitatSpecies.length === 0) return null;
                  const discoveredCount = habitatSpecies.filter((s) => s.discovered).length;
                  const isExpanded = expandedHabitats.has(habitat);

                  return (
                    <div key={habitat} className="overflow-hidden rounded-xl border border-border bg-card">
                      <button
                        type="button"
                        onClick={() => toggleHabitat(habitat)}
                        className="flex w-full items-center justify-between px-4 py-3"
                        aria-expanded={isExpanded}
                      >
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-foreground">
                            {t(`catalog.habitat${habitat.charAt(0).toUpperCase() + habitat.slice(1)}`, {
                              defaultValue: habitat.charAt(0).toUpperCase() + habitat.slice(1),
                            })}
                          </h3>
                          <span className="text-xs text-muted-foreground">
                            {t('catalog.discovered', {
                              defaultValue: '{{count}}/{{total}} discovered',
                              count: discoveredCount,
                              total: habitatSpecies.length,
                            })}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp size={16} className="text-muted-foreground" />
                        ) : (
                          <ChevronDown size={16} className="text-muted-foreground" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="grid grid-cols-3 gap-3 px-3 pb-3">
                          {habitatSpecies.map((animal) =>
                            animal.discovered ? (
                              <button
                                key={animal.slug}
                                type="button"
                                onClick={() => openDetail(animal.slug)}
                                className="flex flex-col items-center gap-1.5 rounded-xl bg-background p-2 transition-transform active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest"
                              >
                                <div className="relative h-20 w-20 overflow-hidden rounded-lg bg-stone-50">
                                  <img
                                    src={animal.thumbPath}
                                    alt={animal.name}
                                    className="h-full w-full object-contain p-1"
                                    loading="lazy"
                                  />
                                </div>
                                <span className="w-full truncate text-center text-xs font-semibold text-foreground">
                                  {animal.name}
                                </span>
                              </button>
                            ) : (
                              <div
                                key={animal.slug}
                                className="flex flex-col items-center gap-1.5 rounded-xl bg-background p-2"
                              >
                                <div className="relative h-20 w-20 overflow-hidden rounded-lg bg-stone-50">
                                  <img
                                    src={animal.thumbPath}
                                    alt=""
                                    className="h-full w-full object-contain p-1 brightness-0 opacity-30"
                                    loading="lazy"
                                  />
                                </div>
                                <span className="w-full truncate text-center text-xs font-medium text-muted-foreground">
                                  ???
                                </span>
                                <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                                  <Search size={10} />
                                  {t('catalog.scanToDiscover', { defaultValue: 'Scan to discover' })}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail loading overlay */}
      {detailLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
        </div>
      )}

      {/* Detail modal */}
      <AnimalDetailModal animal={selectedAnimal} onClose={() => setSelectedAnimal(null)} />

      {/* Onboarding overlay (first visit) */}
      <AnimatePresence>
        {showOnboarding && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            onKeyDown={(e) => {
              if (e.key === 'Escape') dismissOnboarding();
            }}
            tabIndex={-1}
            ref={(el) => el?.focus()}
          >
            <motion.div
              className="relative mx-6 max-w-sm w-full rounded-2xl bg-background p-6 text-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={dismissOnboarding}
                className="absolute top-3 end-3 p-1 rounded-full hover:bg-muted"
                aria-label={t('common:close', { defaultValue: 'Close' })}
              >
                <X size={16} />
              </button>
              {[
                {
                  icon: Trees,
                  title: t('onboarding.step1Title', { defaultValue: 'Welcome to the Globe' }),
                  body: t('onboarding.step1Body', {
                    defaultValue: 'Every space you scan has an animal. Discover them all.',
                  }),
                  color: 'bg-forest',
                },
                {
                  icon: Heart,
                  title: t('onboarding.step2Title', { defaultValue: 'Keep them thriving' }),
                  body: t('onboarding.step2Body', {
                    defaultValue: 'Animals fade when you stop visiting. A scan brings them back.',
                  }),
                  color: 'bg-gold',
                },
                {
                  icon: Compass,
                  title: t('onboarding.step3Title', { defaultValue: 'Build your reserve' }),
                  body: t('onboarding.step3Body', {
                    defaultValue: '240 species are out there. How many can you find?',
                  }),
                  color: 'bg-forest',
                },
              ].map(
                (step, i) =>
                  i === onboardingStep && (
                    <div key={i}>
                      <div
                        className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${step.color}`}
                      >
                        <step.icon size={24} className="text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground mb-2">{step.title}</h3>
                      <p className="text-sm text-muted-foreground mb-5">{step.body}</p>
                    </div>
                  ),
              )}
              {/* Dots */}
              <div className="flex justify-center gap-1.5 mb-4">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={`h-1.5 w-1.5 rounded-full transition-colors ${i === onboardingStep ? 'bg-forest' : 'bg-muted'}`}
                  />
                ))}
              </div>
              <button
                onClick={() => {
                  if (onboardingStep < 2) setOnboardingStep(onboardingStep + 1);
                  else dismissOnboarding();
                }}
                className="inline-flex items-center gap-1.5 rounded-xl bg-forest px-5 py-2.5 text-sm font-semibold text-white"
              >
                {onboardingStep < 2 ? (
                  <>
                    {t('common:next', { defaultValue: 'Next' })}
                    <ArrowRight size={14} />
                  </>
                ) : (
                  t('common:getStarted', { defaultValue: 'Get started' })
                )}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
